import { useEffect, useRef, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { Info, LoaderCircle, LockKeyhole } from "lucide-react";
import { getStripePromise } from "../../lib/stripeClient";
import type { PaymentSetup, VerificationSnapshot } from "./verificationApi";

interface Props {
  prepare: () => Promise<PaymentSetup | null>;
  confirm: (id: string) => Promise<VerificationSnapshot | null>;
  onSaved: () => void;
  replacement?: boolean;
  staging?: boolean;
}
export function VerificationCard(props: Props) {
  const [setup, setSetup] = useState<PaymentSetup | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const mounted = useRef(false);
  const prepare = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await props.prepare();
      if (!mounted.current) return;
      if (result) {
        const loadedStripe = await getStripePromise();
        if (!mounted.current) return;
        if (!loadedStripe) throw new Error("Stripe unavailable");
        setStripe(loadedStripe);
        setSetup(result);
      } else
        setError("We couldn’t open the secure card form. Please try again.");
    } catch {
      if (mounted.current)
        setError("We couldn’t open the secure card form. Please try again.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  };
  useEffect(() => {
    mounted.current = true;
    void prepare();
    return () => {
      mounted.current = false;
    };
  }, []);
  if (!setup)
    return (
      <div className="sv-step-body">
        <p role="status">
          {loading ? "Opening your secure card form…" : error}
        </p>
        {!loading && (
          <button
            type="button"
            className="sv-primary"
            onClick={() => void prepare()}
          >
            Try again
          </button>
        )}
      </div>
    );
  return (
    <Elements
      stripe={stripe}
      options={{
        clientSecret: setup.clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            fontFamily: "Inter, system-ui, sans-serif",
            fontSizeBase: "16px",
            colorPrimary: "#17181b",
            borderRadius: "9px",
          },
        },
      }}
    >
      <CardForm {...props} setupIntentId={setup.setupIntentId} />
    </Elements>
  );
}
function CardForm({
  confirm,
  onSaved,
  setupIntentId,
  replacement,
  staging,
}: Props & { setupIntentId: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const save = async () => {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/onboarding?verification=payment`,
        },
        redirect: "if_required",
      });
      if (!mounted.current) return;
      if (result.error) {
        setError(
          result.error.message ||
            "We couldn’t save that card. Please check the details.",
        );
        return;
      }
      const snapshot = await confirm(result.setupIntent?.id || setupIntentId);
      if (!mounted.current) return;
      if (snapshot?.card.status === "saved") onSaved();
      else
        setError(
          "We’re still confirming your card. Your setup is saved; please try again in a moment.",
        );
    } catch {
      if (mounted.current)
        setError(
          "We couldn’t confirm your card. Please try again. Saving a card does not charge it.",
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <div className="sv-step-body">
      {staging && <div className="sv-notice signup-test-card-note"><Info size={18} /><p><strong>Use a Stripe test card.</strong> Enter <strong>4242 4242 4242 4242</strong>, any future expiry date, and any three-digit CVC. Use a valid billing postal code. No real money moves.</p></div>}
      <PaymentElement
        onReady={() => setReady(true)}
        onLoadError={() =>
          setError(
            "The secure card form couldn’t load. Close this window and try again.",
          )
        }
      />
      <div className="sv-notice">
        <LockKeyhole size={17} />
        <p>
          {staging ? (
            "Your test subscription starts after simulated approval. This account cannot send texts to customers."
          ) : replacement ? (
            "We’ll securely save your new card. After saving, return to this page to retry your first payment."
          ) : (
            <>
              Your card is for your subscription.{" "}
              <strong>
                Nothing is charged until your number is approved and ready to
                text.
              </strong>{" "}
              You’ll confirm your plan before we submit.
            </>
          )}
        </p>
      </div>
      {error && (
        <p className="sv-error" role="alert">
          {error}
        </p>
      )}
      <footer className="sv-form-footer">
        <span className="sv-security">
          <LockKeyhole size={15} />
          Secured by Stripe
        </span>
        <button
          type="button"
          className="sv-primary"
          disabled={!stripe || !elements || !ready || busy}
          onClick={() => void save()}
        >
          {busy ? (
            <>
              <LoaderCircle size={17} className="sv-search-spinner" />
              Saving card…
            </>
          ) : replacement ? (
            "Save new card"
          ) : (
            "Save card & continue"
          )}
        </button>
      </footer>
    </div>
  );
}
